import ReactGA from "react-ga4";
import { isAnalyticsDebug } from "common/analytics";

// GA4 collection limits: names beyond these are rejected or truncated by GA.
const MAX_EVENT_NAME = 40;
const MAX_USER_PROPERTY_NAME = 24;
const MAX_EVENT_PARAM_VALUE = 100;
const MAX_USER_PROPERTY_VALUE = 36;
const PAGE_TITLE_WAIT_MS = 1000;

export const GA4_EVENT_ALIASES: Readonly<Record<string, string>> = {
  contribution_progress_share_cta_activated: "contrib_progress_share_cta_activated",
};

export const GA4_USER_PROPERTY_ALIASES: Readonly<Record<string, string>> = {};

// Google's policies forbid PII in GA, and accessibility identity is health data.
export const GA4_EXCLUDED_USER_PROPERTIES: ReadonlySet<string> = new Set([
  "email",
  "name",
  "username",
  "accessibility_profile",
  "saved_accessibility_filter",
]);

// Typed free text can contain an email or a name.
export const GA4_EXCLUDED_EVENT_PARAMS: ReadonlySet<string> = new Set(["search_query"]);

// Route params holding a secret or an email; GA gets a placeholder instead.
const SENSITIVE_PATHS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/login\/[^/]+/, "/login/(email)"],
  [/^\/(passwordreset|verifyuser)\/[^/]+\/user\/[^/]+/, "/$1/(code)/user/(email)"],
  [/^\/share-token\/[^/]+/, "/share-token/(token)"],
];
// The query string carries OAuth codes and typed search text; only GA's campaign attribution survives.
const CAMPAIGN_PARAM = /^(utm_\w+|gclid|gbraid|wbraid|dclid)$/;
// URL-valued props (entry_page_url, referrers, host URLs) carry what page_location strips.
const URL_VALUE = /^https?:\/\//i;

type Ga4Value = string | number;

let ga4Ready = false;
let currentUserId: string | null = null;
let lastPageViewLocation: string | null = null;
let pendingPageView: ReturnType<typeof setTimeout> | null = null;
// gtag('set') does not attach custom keys to GA4 events, so super props are merged into each hit.
const registeredSuperProps: Record<string, Ga4Value> = {};
const userPropKeys = new Set<string>();

function warn(action: string, err: unknown): void {
  if (isAnalyticsDebug()) console.warn(`[analytics:ga4] ${action} failed`, err);
}

function fitName(name: string, aliases: Readonly<Record<string, string>>, max: number): string {
  const mapped = aliases[name] ?? name;
  if (mapped.length <= max) return mapped;
  if (isAnalyticsDebug()) {
    console.warn(`[analytics:ga4] "${name}" exceeds GA's ${max}-char limit — add an alias`);
  }
  return mapped.slice(0, max);
}

export function toGa4EventName(name: string): string {
  return fitName(name, GA4_EVENT_ALIASES, MAX_EVENT_NAME);
}

export function toGa4UserPropertyName(name: string): string {
  return fitName(name, GA4_USER_PROPERTY_ALIASES, MAX_USER_PROPERTY_NAME);
}

// null means "no value": dropped from an event, but sent on `set` because that is how GA clears one.
function toGa4Value(value: unknown, maxLen: number): Ga4Value | null {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value) && value.length === 0) return null;
  if (typeof value === "number") return value;
  const text = Array.isArray(value) ? value.join(",") : String(value);
  if (!URL_VALUE.test(text)) return text.slice(0, maxLen);
  try {
    return toGa4PageLocation(text).slice(0, maxLen);
  } catch {
    return null;
  }
}

export function toGa4EventParams(props: Record<string, unknown>): Record<string, Ga4Value> {
  const params: Record<string, Ga4Value> = {};
  for (const [key, value] of Object.entries(props)) {
    if (GA4_EXCLUDED_EVENT_PARAMS.has(key)) continue;
    const v = toGa4Value(value, MAX_EVENT_PARAM_VALUE);
    if (v !== null) params[key] = v;
  }
  return params;
}

export function toGa4SuperProperties(props: Record<string, unknown>): Record<string, Ga4Value | null> {
  const params: Record<string, Ga4Value | null> = {};
  for (const [key, value] of Object.entries(props)) {
    if (GA4_EXCLUDED_EVENT_PARAMS.has(key)) continue;
    params[key] = toGa4Value(value, MAX_EVENT_PARAM_VALUE);
  }
  return params;
}

export function toGa4PageLocation(href: string): string {
  const url = new URL(href);
  let path = url.pathname;
  for (const [pattern, replacement] of SENSITIVE_PATHS) path = path.replace(pattern, replacement);
  path = path
    .split("/")
    .map((segment) => (/@|%40/i.test(segment) ? "(email)" : segment))
    .join("/");
  const campaign = new URLSearchParams();
  for (const [key, value] of url.searchParams) {
    if (CAMPAIGN_PARAM.test(key)) campaign.append(key, /@|%40/i.test(value) ? "(email)" : value);
  }
  const query = campaign.toString();
  return `${url.origin}${path}${query ? `?${query}` : ""}`;
}

// The search page quotes the typed query in its title; first-to-last quote also covers a query containing quotes.
export function toGa4PageTitle(title: string): string {
  return title.replace(/"[\s\S]*"/, '"(query)"').replace(/[^\s"]+@[^\s"]+/g, "(email)");
}

function toGa4Referrer(referrer: string): string {
  if (!referrer) return "";
  try {
    return toGa4PageLocation(referrer);
  } catch {
    return "";
  }
}

export function toGa4UserProperties(props: Record<string, unknown>): Record<string, Ga4Value | null> {
  const params: Record<string, Ga4Value | null> = {};
  for (const [key, value] of Object.entries(props)) {
    if (GA4_EXCLUDED_USER_PROPERTIES.has(key)) continue;
    params[toGa4UserPropertyName(key)] = toGa4Value(value, MAX_USER_PROPERTY_VALUE);
  }
  return params;
}

// Raw gtag only: ReactGA.event/set rename keys (page → page_path, userId → user_id).
function gtag(...args: unknown[]): void {
  ReactGA.gtag(...args);
}

// gtag otherwise sends the raw document URL, title and referrer with every hit.
function sendEvent(name: string, params: Record<string, Ga4Value>): void {
  gtag("event", name, {
    ...registeredSuperProps,
    ...params,
    page_location: toGa4PageLocation(window.location.href),
    page_title: toGa4PageTitle(document.title),
    page_referrer: toGa4Referrer(document.referrer),
  });
}

// Local state is cleared before gtag so a throwing gtag cannot carry one user's state into the next.
function clearUser(): void {
  const clearedUser: Record<string, null> = {};
  for (const key of userPropKeys) clearedUser[key] = null;
  userPropKeys.clear();
  currentUserId = null;
  gtag("set", { user_id: null });
  if (Object.keys(clearedUser).length > 0) gtag("set", "user_properties", clearedUser);
}

export function initGa4(measurementId: string): void {
  try {
    ReactGA.initialize(measurementId, {
      gtagOptions: {
        // Page views are sent by ga4PageView once page_name is current, not on gtag config.
        send_page_view: false,
        // GA treats any debug_mode value, even false, as a DebugView hit.
        ...(import.meta.env.DEV ? { debug_mode: true } : {}),
      },
    });
    ga4Ready = true;
  } catch (err) {
    warn("init", err);
  }
}

export function ga4Track(name: string, props: object): void {
  if (!ga4Ready) return;
  try {
    sendEvent(toGa4EventName(name), toGa4EventParams(props as Record<string, unknown>));
  } catch (err) {
    warn("track", err);
  }
}

export function ga4PageView(): void {
  if (!ga4Ready || typeof window === "undefined") return;
  try {
    const location = toGa4PageLocation(window.location.href);
    if (location === lastPageViewLocation) return;
    // Route titles commit after router onLoad, so until it changes the title is the previous page's.
    const staleTitle = lastPageViewLocation === null ? "" : document.title;
    lastPageViewLocation = location;
    if (pendingPageView !== null) clearTimeout(pendingPageView);
    const superProps = { ...registeredSuperProps };
    const startedAt = Date.now();
    const send = () => {
      pendingPageView = null;
      try {
        const title = document.title;
        if ((!title || title === staleTitle) && Date.now() - startedAt < PAGE_TITLE_WAIT_MS) {
          pendingPageView = setTimeout(send, 100);
          return;
        }
        gtag("event", "page_view", {
          ...superProps,
          page_location: location,
          page_title: toGa4PageTitle(title),
          page_referrer: toGa4Referrer(document.referrer),
        });
      } catch (err) {
        warn("pageView", err);
      }
    };
    send();
  } catch (err) {
    warn("pageView", err);
  }
}

export function ga4Identify(distinctId: string): void {
  if (!ga4Ready) return;
  try {
    // Without Mixpanel, identifyWithReset never resets, so a switched account would inherit the last profile.
    // Super props describe the device and page, not the user, so they survive the switch.
    if (currentUserId !== null && currentUserId !== distinctId) clearUser();
    currentUserId = distinctId;
    gtag("set", { user_id: distinctId });
  } catch (err) {
    warn("identify", err);
  }
}

export function ga4Register(superProps: object): void {
  if (!ga4Ready) return;
  try {
    const params = toGa4SuperProperties(superProps as Record<string, unknown>);
    for (const [key, value] of Object.entries(params)) {
      if (value === null) delete registeredSuperProps[key];
      else registeredSuperProps[key] = value;
    }
  } catch (err) {
    warn("register", err);
  }
}

export function ga4SetUserProperties(userProps: object): void {
  if (!ga4Ready) return;
  try {
    const params = toGa4UserProperties(userProps as Record<string, unknown>);
    if (Object.keys(params).length === 0) return;
    for (const key of Object.keys(params)) userPropKeys.add(key);
    gtag("set", "user_properties", params);
  } catch (err) {
    warn("setUserProperties", err);
  }
}

// GA's client_id (_ga cookie) survives this; gtag offers no supported way to rotate it.
export function ga4Reset(): void {
  if (!ga4Ready) return;
  try {
    for (const key of Object.keys(registeredSuperProps)) delete registeredSuperProps[key];
    clearUser();
  } catch (err) {
    warn("reset", err);
  }
}
